<template>
  <div class="kk-designer__info">
    <div class="kk-designer__formulabox">
      <input
        class="kk-designer__process-design-title"
        :value="showText"
        :disabled="formulaBarDisabled"
        data-test-id="formula-bar"
        @keyup.enter="enter"
        @input="editCellText"
        @focus="setValue"
        @keyup="escape"
        @blur="blur"
      />
      <i class="icon-Expand kk-designer__formulabox--icon" @click="showFormulaBox"></i>
    </div>

    <custom-icon
      v-if="(current.linked_by_activities || []).length"
      class-name="icon-link-variant kk-designer__process-design--linked"
      icon-color="#083277"
    />

    <div v-if="accessToolbarItems.length > 0" class="kk-designer__components-container">
      <div
        v-for="(item, index) in accessToolbarItems"
        :key="item"
        class="kk-designer__component-item"
        :data-test-id="`${accessToolbarComponents[item]?.name}-command-item`"
      >
        <tooltip v-if="showTooltip" :text="formatNumber(index + 1)" />
        <component :is="accessToolbarComponents[item]?.name" ref="dynamicComponent" />
      </div>
    </div>
    <section v-if="showTableSettings" class="kk-designer__table-settings"></section>

    <span style="flex-grow: 1"></span>
  </div>
</template>

<script>
import DesignerMixin from "./DesignerMixin";
import DesignModeSelect from "./access-toolbar/DesignModeSelect.vue";
import ViewList from "./views/ViewList.vue";
import FlowBtn from "./access-toolbar/FlowBtn.vue";
import Screenshot from "./access-toolbar/Screenshot.vue";
import FavoriteProcess from "./access-toolbar/FavoriteProcess.vue";
import Fullscreen from "./access-toolbar/Fullscreen.vue";
import Copy from "./access-toolbar/Copy.vue";
import Cut from "./access-toolbar/Cut.vue";
import Paste from "./access-toolbar/Paste.vue";
import Undo from "./access-toolbar/Undo.vue";
import Redo from "./access-toolbar/Redo.vue";
import Merge from "./access-toolbar/Merge.vue";
import Formatting from "./access-toolbar/Formatting.vue";
import CustomStyles from "./access-toolbar/CustomStyles.vue";
import AddComment from "./access-toolbar/AddComment.vue";
import DeleteComment from "./access-toolbar/DeleteComment.vue";
import FormatPainter from "./access-toolbar/FormatPainter.vue";
import Unmerge from "./access-toolbar/Unmerge.vue";
import Tooltip from "../commons/Tooltip.vue";
import {
  AllFonts,
  BgColors,
  CellIconNames,
  CellIcons,
  EditModes,
  HotkeyScopes,
  TextColors,
} from "../../utils/constants";
import hotkeys from "hotkeys-js";
import CustomIcon from "../commons/CustomIcon.vue";
import CustomButton from "../commons/CustomButton.vue";
import GotoToolBox from "./access-toolbar/GotoToolBox.vue";
import Fill from "./access-toolbar/Fill.vue";
import StructureTable from "./access-toolbar/StructureTable.vue";
import { ProcessTypes } from "../../utils/icons";
import { getEditModeValue } from "../../utils/DataTypeUtils";
export default {
  components: {
    CustomButton,
    CustomIcon,
    DesignModeSelect,
    ViewList,
    FlowBtn,
    FavoriteProcess,
    Screenshot,
    Fullscreen,
    Copy,
    Cut,
    Paste,
    Undo,
    Redo,
    Tooltip,
    Merge,
    AddComment,
    DeleteComment,
    Formatting,
    CustomStyles,
    GotoToolBox,
    Fill,
    StructureTable,
    FormatPainter,
    Unmerge,
  },
  mixins: [DesignerMixin],
  props: ["designId"],
  data() {
    return {
      showTooltip: false,
      keyPressed: null,
      originalText: "",
    };
  },
  computed: {
    formulaBarDisabled() {
      return !this.mainRange?.range;
    },
    action() {
      return this.pdesign.action;
    },
    activeCell() {
      const cellId = this.mergedCellIds[this.activeCellId]?.replace("_merged", "") || this.activeCellId;
      return this.cellsMetaData[cellId] || {};
    },
    accessToolbarItems() {
      return this.$store.state.preferences.allPreferences.accessToolbar ?? [];
    },
    accessToolbarComponents() {
      return this.$store.state.preferences.accessToolbarComponents;
    },
    editCell: {
      get() {
        return this.$store.state.cells.editCell;
      },
      set(value) {
        this.$store.state.cells.editCell = value;
      },
    },
    showText() {
      if (!this.firstCellId) return "";
      const [columnIndex, rowIndex] = this.getCellIndexById(this.firstCellId);
      const currentDataType = this.$store.getters["rangeStyle/dataType"](columnIndex, rowIndex);
      return getEditModeValue(currentDataType, this.editCell.text) || "";
    },
    isAltPressed() {
      return this.$store.state.pdesign.isAltPressed;
    },
    editMode() {
      return this.$store.state.pdesign.editMode.value;
    },
    showTableSettings() {
      return this.$store.state.table.activeTable !== null;
    },
    firstCellId() {
      if (!this.mergedCellIds) return;
      return this.mergedCellIds[this.activeCellId]?.replace("_merged", "") || this.activeCellId;
    },
  },
  watch: {
    isAltPressed(newValue, oldValue) {
      if (!newValue) this.showTooltip = newValue; // Should hide when enter is pressed while editing.
    },
  },
  mounted() {
    hotkeys("*", HotkeyScopes.QuickAccessToolbar, (event, handler) => {
      event.preventDefault();
      const isNumKey = event.code.startsWith("Numpad") || event.code.startsWith("Digit");
      if (!isNumKey) return;

      if (this.keyPressed === null) return (this.keyPressed = event.key);
      const shortCut = this.keyPressed + event.key;

      // reset if shortcut is invalid.
      if (!this.$refs.dynamicComponent[this.unformatNumber(+shortCut) - 1]) return (this.keyPressed = null);
      // call function related to the key press (shortCut).
      this.$refs.dynamicComponent[this.unformatNumber(+shortCut) - 1].callFunc();

      // reset values.
      this.showTooltip = false;
      this.keyPressed = null;
    });
    hotkeys("up, down, right, left", HotkeyScopes.QuickAccessToolbar, (event, handler) => {
      event.preventDefault();
    });
  },
  beforeUnmount() {
    hotkeys.unbind("*,up, down, right, left", HotkeyScopes.QuickAccessToolbar);
  },
  methods: {
    showFormulaBox() {
      this.$store.dispatch("pdesign/toggleFormulaBox");
    },
    escape(event) {
      if (event.key === "Escape") {
        this.editCell = { ...this.editCell, text: this.originalText };
        this.$store.dispatch("cells/setEditCell", { text: this.originalText });
        this.$store.dispatch("cells/editCellContent", { id: this.activeCellId, value: this.originalText });
      }
    },
    setValue(e) {
      this.originalText = e.target.value;
    },
    editCellText(e) {
      e.preventDefault();
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(this.firstCellId));
      this.$store.dispatch("cells/setEditCell", { text: e.target.value, range: "", focus: true });
    },
    async enter(e) {
      e.target.blur();
      this.$store.dispatch("cells/editCellContent", { id: this.firstCellId, value: e.target.value });
    },
    async blur(e) {
      let isHeaderValid = true;
      if (this.current.type === ProcessTypes.Grid && this.tableHeaders[this.activeCellId]) {
        isHeaderValid = await this.$store.dispatch("table/validateAndUpdateHeader", {
          text: e.target.value,
          cellId: this.activeCellId,
          originalText: this.originalText,
        });
      }
      if (isHeaderValid) {
        this.$store.dispatch("cells/editCellContent", { id: this.activeCellId, value: e.target.value });
      } else {
        this.$store.dispatch("cells/editCellContent", { id: this.activeCellId, value: this.originalText });
      }
      hotkeys.trigger("enter", HotkeyScopes.ProcessDesigner);
    },
    formatNumber(number) {
      return number < 10 ? `0${number}` : number;
    },
    unformatNumber(number) {
      return number[0] === 0 ? number[1] : number;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/styles/variables";

.kk-designer {
  &__formulabox {
    display: flex;
    align-items: center;
    position: relative;
    &--icon {
      position: absolute;
      right: 10px;
      font-size: 21px;
      &:hover {
        color: rgba(211, 227, 249, 1) !important;
      }
    }
    &:focus-within {
      .kk-designer__formulabox--icon {
        -webkit-text-stroke-width: 0.3px;
        color: @kk-color-primary-blue;
      }
    }
  }

  &__table-settings {
    margin-left: 1rem;
  }

  &__info {
    display: flex;
    gap: 1rem;
    align-items: center;
    padding: 1.2rem 1rem 1.2rem 0;
    width: 100%;
    position: sticky;
    left: 0;
    z-index: 100;
  }

  &__process-design-title {
    color: @kk-color-dark;
    font-weight: bold;
    font-size: 1rem;
    line-height: 1.5;
    background-color: @kk-color-designer-background;
    padding: 0.35rem 1rem;
    padding-right: 1.8rem;
    border: 2px solid transparent;
    border-radius: @kk-border-radius;
    margin-right: 0.25rem;
    width: 20rem;
    overflow: hidden;
    white-space: nowrap;
    border: 1.5px solid transparent;

    &:focus {
      border: 1.5px solid rgba(8, 50, 119, 0.69);
      caret-color: rgba(8, 50, 119, 0.35); /* Change the cursor color when focused */
    }
  }
  &__process-design--linked {
    color: @kk-color-primary-blue;
  }

  &__components-container {
    padding: 0.3rem;
    border-radius: @kk-border-radius;
    display: flex;
    gap: 0.2rem;
    align-items: center;
    background-color: rgba(248, 248, 251, 0.4980392156862745);
  }

  &__component-item {
    position: relative; // for tooltips
    font-size: large;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: @kk-border-radius;
    color: @kk-color-gray;
    min-width: 28px;
    height: 28px;
    cursor: pointer;
    & .toolbar-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
    }
    &:hover {
      color: @kk-color-primary-blue;
      background-color: rgba(8, 50, 119, 0.09803921568627451);
    }
  }
}
</style>
